import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import HVIHeader from "@/components/HVIHeader";
import HVIFooter from "@/components/HVIFooter";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/status")({
  head: () => ({
    meta: [
      { title: "System Status — Hantavirus International" },
      { name: "description", content: "Live operational status for Hantavirus International data feeds and services." },
      { name: "keywords", content: "hantavirus status, hantavirus tracker status, hantavirus international, hantavirus system status, hantavirus data feeds" },
      { name: "robots", content: "index, follow" },
    ],
  }),
  component: StatusPage,
});

type Check = { name: string; ok: boolean | null; detail?: string };

function StatusPage() {
  const [checks, setChecks] = useState<Check[]>([
    { name: "Web frontend", ok: true, detail: "If you can read this, the frontend is up." },
    { name: "Database (cases)", ok: null },
    { name: "Database (news)", ok: null },
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cases = await supabase.from("cases").select("id", { count: "exact", head: true });
      const news = await supabase.from("news").select("id", { count: "exact", head: true });
      if (cancelled) return;
      setChecks((prev) =>
        prev.map((c) => {
          if (c.name === "Database (cases)")
            return { ...c, ok: !cases.error, detail: cases.error ? "Connection error" : `${cases.count ?? 0} records` };
          if (c.name === "Database (news)")
            return { ...c, ok: !news.error, detail: news.error ? "Connection error" : `${news.count ?? 0} records` };
          return c;
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const allOk = checks.every((c) => c.ok === true);
  const anyDown = checks.some((c) => c.ok === false);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <HVIHeader />
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 space-y-6">
          <header className="border-b border-border pb-4">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">System Status</h1>
            <p
              className={`text-sm mt-2 uppercase tracking-wider font-bold ${
                anyDown ? "text-destructive" : allOk ? "text-hvi-primary" : "text-muted-foreground"
              }`}
            >
              {anyDown ? "● Degraded" : allOk ? "● All systems operational" : "● Checking…"}
            </p>
          </header>

          <ul className="border border-border divide-y divide-border">
            {checks.map((c) => (
              <li key={c.name} className="flex items-center justify-between gap-4 px-4 py-3">
                <div>
                  <div className="text-sm font-bold">{c.name}</div>
                  {c.detail && <div className="text-xs text-muted-foreground mt-0.5">{c.detail}</div>}
                </div>
                <span
                  className={`text-[11px] uppercase tracking-wider font-bold px-2 py-1 border ${
                    c.ok === true
                      ? "border-hvi-primary text-hvi-primary"
                      : c.ok === false
                      ? "border-destructive text-destructive"
                      : "border-border text-muted-foreground"
                  }`}
                >
                  {c.ok === true ? "Operational" : c.ok === false ? "Down" : "Checking"}
                </span>
              </li>
            ))}
          </ul>

          <p className="text-xs text-muted-foreground">
            This page checks live database connectivity from your browser. Status is computed at page load.
          </p>
        </div>
      </main>
      <HVIFooter />
    </div>
  );
}
